import { toHexFromId } from '#~/api/index.js';
import type {
  AgentRegistryGetOrganizationResponse,
  AgentRegistryGetRoomResponse
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { Agent } from '@studio/models/agent';
import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';
import type { User } from '@studio/models/user';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isNotEmpty } from '@tryiris-ai/js-types';
import type { Auth0OrganizationData } from './utils.js';

type StudioUser = User & { organizationData?: Auth0OrganizationData };

type AgentSummary = { embedded: boolean; name: string };

export const toRoomRecordMap = (
  rooms: (AgentRegistryGetRoomResponse & {
    bootstrapInventoryOnly?: boolean;
    initialBillingPending?: boolean | undefined;
  })[]
): Record<RoomRecord['id'], RoomRecord> =>
  rooms.reduce<Record<RoomRecord['id'], RoomRecord>>((accumulator, room) => {
    const id = toHexFromId<RoomRecord['id']>(room.id);

    if (!id) {
      return accumulator;
    }

    const agents = room.agents.reduce<Record<Agent['id'], AgentSummary>>(
      (agentAccumulator, agent) => {
        const agentId = toHexFromId<Agent['id']>(agent.id);

        if (!agentId) {
          return agentAccumulator;
        }

        agentAccumulator[agentId] = {
          embedded: agent.embedded,
          name: agent.name
        };

        return agentAccumulator;
      },
      {}
    );

    const organizationId = toHexFromId<Organization['id']>(room.organizationId);
    if (!organizationId) {
      return accumulator;
    }

    // The registry sends created_at as Unix seconds (CreatedAt.Unix()); room
    // records carry JS milliseconds, the unit Date and Intl expect.
    let createdAt: number | undefined;
    if (room.createdAt !== undefined) {
      createdAt = room.createdAt * 1000;
    }

    accumulator[id] = {
      agents,
      id,
      name: room.name,
      organizationId,
      permissions: room.permissions ?? {},
      ...(room.initialBillingPending !== undefined && {
        initialBillingPending: room.initialBillingPending
      }),
      ...(createdAt !== undefined && { createdAt }),
      ...(room.bootstrapInventoryOnly === true && {
        registryHydration: 'authoritative_placeholder' as const
      })
    };

    return accumulator;
  }, {});

/** Replace only primary identity placeholders once replica records hydrate. */
export const mergeHydratedBootstrapRoomRecords = (
  current: Record<RoomRecord['id'], RoomRecord>,
  hydrated: Record<RoomRecord['id'], RoomRecord>
): Record<RoomRecord['id'], RoomRecord> => {
  let merged: Record<RoomRecord['id'], RoomRecord> | undefined;
  for (const [roomId, room] of Object.entries(hydrated) as [RoomRecord['id'], RoomRecord][]) {
    if (current[roomId]?.registryHydration !== 'authoritative_placeholder') continue;
    merged ??= { ...current };
    merged[roomId] = {
      ...room,
      ...(current[roomId]?.initialBillingPending !== undefined && {
        initialBillingPending: current[roomId]!.initialBillingPending
      })
    };
  }
  return merged ?? current;
};

export const toOrganizationMap = (
  organizations: AgentRegistryGetOrganizationResponse[],
  user: T_Maybe<StudioUser>
): Record<Organization['id'], Organization> =>
  organizations.reduce<Record<Organization['id'], Organization>>((accumulator, organization) => {
    const id = toHexFromId<Organization['id']>(organization.id);

    if (!id) {
      return accumulator;
    }

    accumulator[id] = {
      id,
      name: isNotEmpty(organization.name) ? organization.name : '',
      website: isNotEmpty(organization.website) ? organization.website : '',
      industry: isNotEmpty(organization.industry)
        ? organization.industry
        : (user?.organizationData?.industry ?? ''),
      photoUrl: isNotEmpty(organization.profilePhotoUrl) ? organization.profilePhotoUrl : '',
      permissions: organization.permissions ?? {}
    };

    return accumulator;
  }, {});
